import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import './Cbac.css';
import Select, { components } from 'react-select';
import { ncdAPI } from "../../../services/ncd.api";
import { formatToInputDate, formatToApiDate } from "../../../helpers/common-helper";

const earlyDetectionImagesBase = [
    "/images/recurrent_numbness_on_sole_palm.png", "/images/tingling_numbness_in_hand_feet.png", "/images/cloudy_blurred_vision.png", "/images/difficulty_in_reading.png", "/images/pain_in_eye.png", "/images/pain_in_eye.png", "/images/difficulty_in_hearing.png", "/images/pain_while_chewing.png", "/images/shortness-of-breath.png", "/images/cough.png", "/images/blood-in-sputum.png", "/images/fever-for-two-weeks.png", "/images/loss-of-weight.png", "/images/night-sweats.png",
    "/images/anti-tb-drug.png", "/images/anyone-in-family-tb.png", "/images/history-of-tb.png", "/images/difficulty-in-opening-mouth.png", "/images/ulcer_in_mouth.png", "/images/any_growth_in_mouth_no_healing.png", "/images/any_white_or_red_patch_in_mouth.png", "/images/sudden-change-in-voice.png", "/images/any-patch-discolor.png", "/images/difficulty_in_holding_objects.png",
    "/images/recurrent_numbness_on_sole_palm.png", "/images/any_thickened_skin.png", "/images/any_nodules_on_skin.png", "/images/clawing_of_fingers.png", "/images/tingling_numbness_in_hand_feet.png", "/images/inability_to_close_eyelid.png", "/images/weakness_in_feet_that_causes_difficulty_to_walk.png", "/images/history-of-fits.png"
];

const earlyDetectionImagesAbove60 = [
    "/images/feeling_unsteady.png", "/images/suffering_from_physical_disability.png", "/images/need_help_from_others.png", "/images/forgetting_names.png"
];

const earlyDetectionImagesFemale = [
    "/images/breast_lump.png", "/images/redness.png", "/images/change_in_size.png", "/images/bleeding_between_periods.png", "/images/post_menopausal_bleeding.png", "/images/bleeding_after_intercourse.png", "/images/foul_smell.png"
];

const smokingValueMap = {
    "Never": 0,
    "Used to consume in the past/Sometimes now": 1,
    "Daily": 2
};

const testArr = {
    "Not at all": "0",
    "Several days": "1",
    "More than half the days": "2",
    "Nearly everyday": "3"
};

export default function CBACform({ hideSection, showSection, individualId, profileData, setShowCBAC, setShowNcdScreening, famIndividualInfo, onCBACSuccess }) {

    const calculateAge = (dob) => {
        if (!dob) return "";
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age.toString();
    };

    const [age, setAge] = useState("");

    useEffect(() => {
        if (profileData) {
            const calculatedAge = profileData.age || calculateAge(profileData.dob) || "";
            setAge(calculatedAge);
        }
    }, [profileData]);


    // Build earlyDetectionItems dynamically based on age and gender
    const { earlyDetectionItems, earlyDetectionImages } = useMemo(() => {
        const baseEarlyDetectionItems = [
            { name: "ulcerPalmSole", label: "Recurrent ulceration in Palm or Sole" },
            { name: "tinglingPalmSole", label: "Recurrent tingling in Palm(s) or Sole(s)" },
            { name: "blurredVision", label: "Cloudy or blurred vision" },
            { name: "diffReading", label: "Difficulty in reading" },
            { name: "eyePain", label: "Pain in eye lasting for more than a week" },
            { name: "eyeRedness", label: "Redness in eyes lasting for more than a week" },
            { name: "diffHearing", label: "Difficulty in hearing" },
            { name: "oralChewPain", label: "Pain while chewing" },
            { name: "breathShrtns", label: "Shortness of breath" },
            { name: "coughing", label: "Coughing more than 2 weeks" },
            { name: "bloodSputum", label: "Blood in sputum" },
            { name: "fever", label: "Fever for > 2 weeks" },
            { name: "weightLoss", label: "Loss of weight" },
            { name: "nightSweat", label: "Night sweats" },
            { name: "antiTB", label: "Are you currently taking anti TB drugs" },
            { name: "familyTB", label: "Anyone in family currently suffering from TB" },
            { name: "hstryTB", label: "History of TB" },
            { name: "diffOpngMouth", label: "Difficulty in opening mouth" },
            { name: "oralUlcer", label: "Any ulcers in mouth that has not healed in two weeks" },
            { name: "oralGrowth", label: "Any growth in mouth that has not healed in two weeks" },
            { name: "oralPatch", label: "Any white or red patch in mouth that has not healed in two weeks" },
            { name: "changeInTone", label: "Any change in tone of your voice" },
            { name: "lossSensation", label: "Any hypopigmented patch(s) or discolored lesion(s) with loss of sensation" },
            { name: "diffHolding", label: "Difficulty in holding objects with fingers" },
            { name: "skinPatch", label: "Recurrent numbness on palm(s) or sole(s)" },
            { name: "skinThickened", label: "Any Thickened Skin" },
            { name: "skinNodule", label: "Any nodules on skin" },
            { name: "clawingHandFeet", label: "Clawing of fingers in hand(s) and/or Feet" },
            { name: "tinglingHandFeet", label: "Tingling numbness in hand and/or feet" },
            { name: "inabltyClsEyelid", label: "Inability to close eyelid" },
            { name: "diffWalking", label: "Weakness in feet that causes difficulty in walking" },
            { name: "histryFits", label: "History of fits" }
        ];

        const ageNum = parseInt(age, 10);
        const above60Items = [
            { name: "unstdyStandWalk", label: "Feeling unsteady while standing or walking" },
            { name: "phyDisbltyMvmnt", label: "Suffering from any physical disability that restricts movement" },
            { name: "dailyActvtyHelp", label: "Needing help from others to perform everyday activities such as eating, getting dressed, grooming, bathing, walking, or using toilets:" },
            { name: "alzheimerSymp", label: "Forgetting names of your near ones or your own home address:" }
        ];

        const isFemale = profileData && profileData.gender && profileData.gender.toLowerCase() === 'female';
        const femaleItems = [
            { name: "breastLump", label: "Lump in breast" },
            { name: "nippleDischarge", label: "Blood stained discharge from nipple" },
            { name: "breastSizeChng", label: "Change in shape and size of breast" },
            { name: "bldngBetwnPeriod", label: "Bleeding between periods" },
            { name: "bldngAftrMenopause", label: "Bleeding after menopause" },
            { name: "bldngAftrIntrcorse", label: "Bleeding after intercourse" },
            { name: "foulSmellVginlDischrg", label: "Foul smelling vaginal discharge" }
        ];

        let items = [...baseEarlyDetectionItems];
        let images = [...earlyDetectionImagesBase];

        if (!isNaN(ageNum) && Number(ageNum) >= 60) {
            items = [...items, ...above60Items];
            images = [...images, ...earlyDetectionImagesAbove60];
        }

        if (isFemale) {
            items = [...items, ...femaleItems];
            images = [...images, ...earlyDetectionImagesFemale];
        }

        return { earlyDetectionItems: items, earlyDetectionImages: images };
    }, [age, profileData]);

    // Calculate age number for use in other parts of the component
    const ageNum = parseInt(age, 10);
    const isFemale = profileData && profileData.gender && profileData.gender.toLowerCase() === 'female';

    // Get the base items for use in JSX
    const baseEarlyDetectionItems = earlyDetectionItems.slice(0, 32); // First 32 items are base items
    const above60Items = earlyDetectionItems.slice(32, 36); // Next 4 items are 60+ items

    const [earlyDetectionResponses, setEarlyDetectionResponses] = useState(Array(earlyDetectionItems.length).fill(""));

    const [cbackdetail, setCbackDetail] = useState(null);

    const [cbacCurDate, setcbacCurDate] = useState(null);
    const [gutkaValue, setGutkaValue] = useState(0);
    const [drinkingscore, setDrnkngScore] = useState(0);
    const [minPhyscalActvtsScore, setMinPhyscalActvtsScore] = useState(0);
    const [familyscore, setFmlyHstryScore] = useState(0);
    const [smkng, setSmkng] = useState(0);
    const [drnkng, setDrnkng] = useState(0);
    const [wstCirmfnc, setWstCirmfnc] = useState(0);
    const [waistconfirm, setWaistconfirm] = useState(0);
    const [minPhyscalActvts, setMinPhyscalActvts] = useState(0);
    const [fmlyHstry, setFmlyHstry] = useState(0);
    const [disabilityType, setDisabilityType] = useState([]);
    const [disabilityTypeDesc, setDisabilityTypeDesc] = useState("");
    const [fuel, setFuel] = useState([]);
    const [fuelother, sethandlefuelOther] = useState("");
    const [feelDepressed, setFeelDepressed] = useState("");
    const [littlePleasure, setLittlePleasure] = useState("");
    const [littleScore, setLittleScore] = useState(0);
    const [feelDepressedScore, setFeelDepressedScore] = useState(0);
    const [dangerScore, setDangerScore] = useState(0);
    const [isPageLoading, setIsPageLoading] = useState(false);

    const [environmentalExposure, setEnvironmentalExposure] = useState({
        cropResidue: "",
        brngGarbg: "",
        smokeExpsure: ""
    });

    const [cropResidue, setCropResidue] = useState(null); // true or false
    const [brngGarbg, setBrngGarbg] = useState(null); // true or false
    const [smokeExpsure, setSmokeExpsure] = useState(null); // true or false

    const [activeSection, setActiveSection] = useState(null);

    useEffect(() => {
        getCback(individualId);
    }, [individualId]);


    useEffect(() => {
        if (disabilityType.includes("None")) {
            setDisabilityTypeDesc("");
        }
    }, [disabilityType]);

    useEffect(() => {

        if (cbackdetail?.dtctnMdl) {
            const responses = earlyDetectionItems.map(item => {
                const value = cbackdetail.dtctnMdl[item.name];
                return value === true ? "Yes" : value === false ? "No" : "";
            });
            setEarlyDetectionResponses(responses);
        }

        let gulval = 0;

        if (cbackdetail?.rskAssmntMdl?.smkng == 'Never') {
            gulval = 0;
        } else if (cbackdetail?.rskAssmntMdl?.smkng == 'Used to consume in the past/Sometimes now') {
            gulval = 1;
        } else if (cbackdetail?.rskAssmntMdl?.smkng == 'Daily') {
            gulval = 2;
        }

        setGutkaValue(gulval);
        setDrnkng(cbackdetail?.rskAssmntMdl?.drnkng === true ? "1" : cbackdetail?.rskAssmntMdl?.drnkng === false ? "0" : "");

        setDrnkngScore(cbackdetail?.rskAssmntMdl?.drnkng === true ? "1" : cbackdetail?.rskAssmntMdl?.drnkng === false ? "0" : "0");

        if (cbackdetail?.rskAssmntMdl?.wstCirmfnc == '90 cm or less' || cbackdetail?.rskAssmntMdl?.wstCirmfnc == '80 cm or less') {
            setWaistconfirm(0);
        }
        if (cbackdetail?.rskAssmntMdl?.wstCirmfnc == '91-100 cm' || cbackdetail?.rskAssmntMdl?.wstCirmfnc == '81-90 cm') {
            setWaistconfirm(1);
        }

        if (cbackdetail?.rskAssmntMdl?.wstCirmfnc == 'More than 100 cm' || cbackdetail?.rskAssmntMdl?.wstCirmfnc == 'More than 90 cm') {
            setWaistconfirm(2);
        }

        setMinPhyscalActvts(cbackdetail?.rskAssmntMdl?.minPhyscalActvts === true ? "0" : cbackdetail?.rskAssmntMdl?.minPhyscalActvts === false ? "1" : "");

        setMinPhyscalActvtsScore(cbackdetail?.rskAssmntMdl?.minPhyscalActvts === true ? "0" : cbackdetail?.rskAssmntMdl?.minPhyscalActvts === false ? "1" : "0");

        setFmlyHstry(cbackdetail?.rskAssmntMdl?.fmlyHstry === true ? "2" : cbackdetail?.rskAssmntMdl?.fmlyHstry === false ? "0" : "");

        setFmlyHstryScore(cbackdetail?.rskAssmntMdl?.fmlyHstry === true ? "2" : cbackdetail?.rskAssmntMdl?.fmlyHstry === false ? "0" : "0");

        // setDisabilityType(cbackdetail?.rskAssmntMdl?.disabilityType || []);
        // setSelectedOptions(cbackdetail?.rskAssmntMdl?.disabilityType || []);

        if (cbackdetail?.rskAssmntMdl?.disabilityType) {

            const apiValues = cbackdetail.rskAssmntMdl.disabilityType;

            // ✅ convert string array → option objects
            const mappedOptions = disabilityOptions.filter(opt =>
                apiValues.includes(opt.value)
            );

            setDisabilityType(apiValues);       // ["Known disability", ...]
            setSelectedOptions(mappedOptions);  // [{value,label}, ...]
        }

        //  setSmkng(smokingValueMap[cbackdetail?.rskAssmntMdl?.smkng] || '');

        setSmkng(cbackdetail?.rskAssmntMdl?.smkng || '');

        setWstCirmfnc(cbackdetail?.rskAssmntMdl?.wstCirmfnc || "");

        setFuel(cbackdetail?.rskAssmntMdl?.fuel || '');

        sethandlefuelOther(cbackdetail?.rskAssmntMdl?.fOther || "");

        if (cbackdetail?.rskAssmntMdl?.cropResidue !== undefined) {
            setCropResidue(cbackdetail.rskAssmntMdl.cropResidue);
        }

        if (cbackdetail?.rskAssmntMdl?.brngGarbg !== undefined) {
            setBrngGarbg(cbackdetail.rskAssmntMdl.brngGarbg);
        }

        if (cbackdetail?.rskAssmntMdl?.smokeExpsure !== undefined) {
            setSmokeExpsure(cbackdetail.rskAssmntMdl.smokeExpsure);
        }

        setLittleScore(testArr[cbackdetail?.dtctnMdl?.littlePleasure] || "0");

        setFeelDepressedScore(testArr[cbackdetail?.dtctnMdl?.feelDepressed] || "0");

        setLittlePleasure(cbackdetail?.dtctnMdl?.littlePleasure || "");

        setFeelDepressed(cbackdetail?.dtctnMdl?.feelDepressed || "");

        setDangerScore(cbackdetail?.dtctnMdl?.mentalHealthScore || 0);

        riskAssessmentScore = cbackdetail?.rskAssmntMdl?.score || 0;

    }, [cbackdetail]);


    const toggleSection = (sectionId) => {
        setActiveSection(prev => (prev === sectionId ? null : sectionId));
    };

    const getCback = async (individualId) => {
        try {

            setIsPageLoading(true);

            const response = await ncdAPI.getCbacDetail(individualId);

            setCbackDetail(response.data);

            if (response?.data?.isCbacComplete) {
                setcbacCurDate(formatToInputDate(response?.data?.rskAssmntMdl?.cbacCurDate));
                setDisabilityTypeDesc(response?.data?.rskAssmntMdl?.disabilityTypeDesc);

                // Only set age from backend if not already set from profileData


                if (famIndividualInfo?.age) {
                    setAge(famIndividualInfo.age);
                } else if (response?.data?.rskAssmntMdl?.age) {
                    setAge(response.data.rskAssmntMdl.age);
                }

                setIsPageLoading(false);
            } else {
                // Only set age from backend if not already set from profileData


                if (famIndividualInfo?.age) {
                    setAge(famIndividualInfo.age);
                } else if (response?.data?.rskAssmntMdl?.age) {
                    setAge(response.data.rskAssmntMdl.age);
                }

                setIsPageLoading(false);
            }

        } catch (error) {
            setIsPageLoading(false);
            console.error("Error in getting details:", error);
        }
    };

    const handleSelectAllNo = () => {
        //  setEarlyDetectionResponses(earlyDetectionItems.reduce((acc, _, index) => ({ ...acc, [index]: "No" }), {}));

        setEarlyDetectionResponses(Array(earlyDetectionItems.length).fill("No"));
    };

    //    --------------------For option js -------------------------------------
    const [selectedOptions, setSelectedOptions] = useState([]);
    const options = [
        "Visible defect",
        "Known disability",
        "Bed ridden",
        "Require support for Activities of Daily Living",
        "None Of the Above"
    ];

    const handleCheckboxChange = (option) => {
        let updatedSelection = [...selectedOptions];

        if (option === "None Of the Above") {
            updatedSelection = updatedSelection.includes(option) ? [] : [option];
        } else {
            if (updatedSelection.includes(option)) {
                updatedSelection = updatedSelection.filter(item => item !== option);
            } else {
                updatedSelection = updatedSelection.filter(item => item !== "None Of the Above");
                updatedSelection.push(option);
            }
        }

        setSelectedOptions(updatedSelection);
    };

    const handleSelectChange = (e) => {
        const selectedValue = e.target.value;
        setSmkng(selectedValue);
        if (smokingValueMap.hasOwnProperty(selectedValue)) {
            setGutkaValue(smokingValueMap[selectedValue]);
        }
    };

    const handleAlcoholChange = (e) => {
        setDrnkng(e.target.value);
        setDrnkngScore(e.target.value);
    };

    const handleWaistChange = (e) => {
        setWstCirmfnc(e.target.value);

        if (e.target.value == '90 cm or less' || e.target.value == '80 cm or less') {
            setWaistconfirm(0);
        }
        if (e.target.value == '91-100 cm' || e.target.value == '81-90 cm') {
            setWaistconfirm(1);
        }

        if (e.target.value == 'More than 100 cm' || e.target.value == 'More than 90 cm') {
            setWaistconfirm(2);
        }

    };

    const handlePhysicalActivitiesChange = (e) => {
        setMinPhyscalActvts(e.target.value);
        setMinPhyscalActvtsScore(e.target.value);
    };

    const handleFamilyHistoryChange = (e) => {
        setFmlyHstry(e.target.value);
        setFmlyHstryScore(e.target.value);
    };

    const handleRadioChange = (index, value) => {
        const updatedResponses = [...earlyDetectionResponses];
        updatedResponses[index] = value;
        setEarlyDetectionResponses(updatedResponses);
    };

    const handleDateChange = (e) => {
        setcbacCurDate(e.target.value); // Update state when user selects a date
    };

    // const handledisabilityType = (selectedOptions) => {

    //     const selectedValues = selectedOptions.map(option => option.value);

    //     if (selectedValues.includes("None Of the Above")) {
    //         // If "None Of the Above" is selected, only keep that one
    //         setDisabilityType(["None Of the Above"]);
    //         setSelectedOptions(["None Of the Above"]);
    //     } else {
    //         // Otherwise, remove "None Of the Above" if previously selected
    //         const filteredValues = selectedValues.filter(val => val !== "None Of the Above");
    //         setDisabilityType(filteredValues);
    //         setSelectedOptions(filteredValues);
    //     }
    // };
    // 

    const handledisabilityType = (selectedOptions) => {
        const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];

        if (selectedValues.includes("None")) {
            // ✅ keep only None
            const noneOption = disabilityOptions.find(opt => opt.value === "None");

            setDisabilityType(["None"]);
            setSelectedOptions([noneOption]); // must be object
        } else {
            // ✅ remove None if other selected
            const filteredOptions = selectedOptions.filter(opt => opt.value !== "None");

            setDisabilityType(filteredOptions.map(opt => opt.value));
            setSelectedOptions(filteredOptions);
        }
    };


    const handleInputChange = (e) => {
        setDisabilityTypeDesc(e.target.value);
    };

    const handleFuel = (selectedOptions) => {
        const values = selectedOptions.map(option => option.value);
        setFuel(values);
    };

    const handlefuelOther = (e) => {
        sethandlefuelOther(e.target.value);
    };

    const handleRadioenexpChange = (e) => {
        setEnvironmentalExposure((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));

        if (e.target.name === 'cropResidue') {
            const value = e.target.value === 'yes'; // convert "yes"/"no" to boolean
            setCropResidue(value);
        }

        if (e.target.name === 'brngGarbg') {
            const value1 = e.target.value === 'yes'; // convert "yes"/"no" to boolean
            setBrngGarbg(value1);
        }

        if (e.target.name === 'smokeExpsure') {
            const value2 = e.target.value === 'yes'; // convert "yes"/"no" to boolean
            setSmokeExpsure(value2);
        }

    };

    const generatePayload = () => {
        return {
            isNewCBAC: true,
            rskAssmntMdl: {
                disabilityType: disabilityType,
                disabilityTypeDesc: disabilityTypeDesc,
                age: age,
                score: riskAssessmentScore,
                cbacCurDate: formatToApiDate(cbacCurDate),
                smkng: smkng,
                drnkng: drnkng === "1" ? true : false,
                wstCirmfnc: wstCirmfnc,
                minPhyscalActvts: minPhyscalActvts === "0" ? true : false,
                fmlyHstry: fmlyHstry === "2" ? true : false,
                cropResidue: environmentalExposure.cropResidue === "yes",
                brngGarbg: environmentalExposure.brngGarbg === "yes",
                smokeExpsure: environmentalExposure.smokeExpsure === "yes",
                fOther: fuelother || null,
                fuel: fuel,
                fuelOther: fuelother || null
            },
            dtctnMdl: {
                ...earlyDetectionResponses.reduce((acc, curr, index) => {
                    const itemName = earlyDetectionItems[index]?.name;
                    acc[itemName] = curr === "Yes";
                    return acc;
                }, {}),
                feelDepressed: feelDepressed,
                littlePleasure: littlePleasure,
                mentalHealthScore: dangerScore
            }
        };
    };

    // Calculate age score based on age group
    let ageScore = 0;
    if (!isNaN(ageNum)) {
        if (ageNum >= 0 && ageNum <= 29) ageScore = 0;
        else if (ageNum >= 30 && ageNum <= 39) ageScore = 1;
        else if (ageNum >= 40 && ageNum <= 49) ageScore = 2;
        else if (ageNum >= 50 && ageNum <= 59) ageScore = 3;
        else if (ageNum >= 60) ageScore = 4;
    }

    // Ensure all scores are numbers and default to 0 if not valid
    const gutkaScore = !isNaN(parseInt(gutkaValue)) ? parseInt(gutkaValue) : 0;
    const drinkingScore = !isNaN(parseInt(drinkingscore)) ? parseInt(drinkingscore) : 0;
    const waistScore = !isNaN(parseInt(waistconfirm)) ? parseInt(waistconfirm) : 0;
    const minPhysicalScore = !isNaN(parseInt(minPhyscalActvtsScore)) ? parseInt(minPhyscalActvtsScore) : 0;
    const familyScore = !isNaN(parseInt(familyscore)) ? parseInt(familyscore) : 0;

    var riskAssessmentScore =
        ageScore +
        gutkaScore +
        drinkingScore +
        waistScore +
        minPhysicalScore +
        familyScore;

    const handleSelectionChange = (e) => {
        const { name, value } = e.target;

        let intValue = 0;

        if (value == 'Not at all') {
            intValue = 0
        }
        if (value == 'Several days') {
            intValue = 1
        }
        if (value == 'More than half the days') {
            intValue = 2
        }
        if (value == 'Nearly everyday') {
            intValue = 3
        }

        if (name === "feelDepressed") {
            setFeelDepressed(value);
            setFeelDepressedScore(intValue);
        }

        if (name === "littlePleasure") {
            setLittlePleasure(value);
            setLittleScore(intValue);
        }

        // Always use numbers during summation
        const feelScore = name === "feelDepressed" ? intValue : Number(feelDepressedScore);
        const littleScoreVal = name === "littlePleasure" ? intValue : Number(littleScore);

        setDangerScore(feelScore + littleScoreVal);
    };

    const [errors, setErrors] = useState({});
    const cbacDateRef = useRef(null);
    const disabilityTypeRef = useRef(null);
    const disabilityTypeDescRef = useRef(null);
    const smkngRef = useRef(null);
    const drnkngRef = useRef(null);
    const wstCirmfncRef = useRef(null);
    const minPhyscalActvtsRef = useRef(null);
    const fmlyHstryRef = useRef(null);
    const fuelRef = useRef(null);
    const fuelotherRef = useRef(null);
    const mentalHealthRef = useRef(null);
    const partARef = useRef(null);
    const partBRef = useRef(null);
    const partCRef = useRef(null);
    const partDRef = useRef(null);


    const scrollToErrorAndOpenAccordion = (errors) => {

        if (!errors || Object.keys(errors).length === 0) return;

        const partAFields = ['smkng', 'drnkng', 'wstCirmfnc', 'minPhyscalActvts', 'fmlyHstry'];
        const partCFields = ['fuel', 'fuelother', 'cropResidue', 'brngGarbg', 'smokeExpsure'];
        const partDFields = ['mentalHealth'];

        // 🔹 Step 2: Check Part A FIRST
        const partAError = partAFields.find(field => errors[field]);

        if (partAError) {
            setActiveSection("partA");

            setTimeout(() => {
                const fieldRefs = {
                    smkng: smkngRef,
                    drnkng: drnkngRef,
                    wstCirmfnc: wstCirmfncRef,
                    minPhyscalActvts: minPhyscalActvtsRef,
                    fmlyHstry: fmlyHstryRef
                };

                fieldRefs[partAError]?.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 300);

            return;
        }

        // 🔹 Step 3: Then check Part B (Early Detection)
        if (errors.earlyDetection) {
            setActiveSection("partB");

            setTimeout(() => {
                const firstErrorIndex = Object.keys(errors.earlyDetection)[0];

                const element = document.getElementsByName(
                    earlyDetectionItems[firstErrorIndex]?.name
                )[0];

                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center"
                    });
                }
            }, 300);

            return;
        }

        // 🔹 Step 4: Then Part C
        const partCError = partCFields.find(field => errors[field]);

        if (partCError) {
            setActiveSection("partC");

            setTimeout(() => {
                partCRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 300);

            return;
        }

        // 🔹 Step 5: Then Part D
        const partDError = partDFields.find(field => errors[field]);

        if (partDError) {
            setActiveSection("partD");

            setTimeout(() => {
                mentalHealthRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 300);

            return;
        }

        // 🔹 Step 6: Fallback (CBAC Date etc.)
        if (errors.cbacCurDate) {
            setTimeout(() => {
                cbacDateRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 300);
        }

        if (errors.disabilityType) {
            setTimeout(() => {
                disabilityTypeRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 300);
        }

        if (errors.disabilityTypeDescRef) {
            setTimeout(() => {
                disabilityTypeDescRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 300);
        }


    };

    const validateCBACForm = () => {
        const newErrors = {};

        if (!cbacCurDate) {
            newErrors.cbacCurDate = "CBAC date is required.";
        }

        if (selectedOptions.length == 0) {
            newErrors.disabilityType = "Select a disability type.";
        }

        // if (
        //     selectedOptions.includes("Visible defect") ||
        //     selectedOptions.includes("Known disability") ||
        //     selectedOptions.includes("Bed ridden") ||
        //     selectedOptions.includes("Require support for Activities of Daily Living")
        // ) {
        //     if (!disabilityTypeDesc || disabilityTypeDesc.trim() === "") {
        //         newErrors.disabilityTypeDesc = "Please describe the disability.";
        //     }
        // }

        if (
            disabilityType.includes("Visible defect") ||
            disabilityType.includes("Known disability") ||
            disabilityType.includes("Bed ridden") ||
            disabilityType.includes("Require support for Activities of Daily Living")
        ) {
            if (!disabilityTypeDesc?.trim()) {
                newErrors.disabilityTypeDesc = "Please describe the disability.";
            }
        }

        if (smkng == '') {
            newErrors.smkng = "Smoking status is required.";
        }

        if (!drnkng) {
            newErrors.drnkng = "Alcohol status is required.";
        }

        if (!wstCirmfnc) {
            newErrors.wstCirmfnc = "Waist circumference is required.";
        }

        if (!minPhyscalActvts) {
            newErrors.minPhyscalActvts = "Physical activity status is required.";
        }

        if (!fmlyHstry) {
            newErrors.fmlyHstry = "Family history is required.";
        }

        if (selectedOptions.length == 0) {
            newErrors.disabilityType = "Select a disability type.";
        }

        if (
            selectedOptions.includes("Visible defect") ||
            selectedOptions.includes("Known disability") ||
            selectedOptions.includes("Bed ridden") ||
            selectedOptions.includes("Require support for Activities of Daily Living")
        ) {
            if (!disabilityTypeDesc || disabilityTypeDesc.trim() === "") {
                newErrors.disabilityTypeDesc = "Please describe the disability.";
            }
        }

        if (fuel.length === 0) {
            newErrors.fuel = "Select a fuel type.";
        }

        if (fuel.includes("Other") && (!fuelother || fuelother.trim() === "")) {
            newErrors.fuelother = "Provide Other fuel details.";
        }

        if (!feelDepressed || !littlePleasure) {
            newErrors.mentalHealth = "Answer both mental health questions.";
        }

        if (!environmentalExposure.cropResidue) {
            newErrors.cropResidue = "Please select crop residue exposure.";
        }

        if (!environmentalExposure.brngGarbg) {
            newErrors.brngGarbg = "Please select burning garbage leaves.";
        }

        if (!environmentalExposure.smokeExpsure) {
            newErrors.smokeExpsure = "Please select working in industries with smoke gas.";
        }

        // Validate Early Detection responses
        const earlyDetectionErrors = {};
        earlyDetectionItems.forEach((item, index) => {
            if (earlyDetectionResponses[index] !== "Yes" && earlyDetectionResponses[index] !== "No") {
                earlyDetectionErrors[index] = "This field is required";
            }
        });

        if (Object.keys(earlyDetectionErrors).length > 0) {
            newErrors.earlyDetection = earlyDetectionErrors;
        }

        return newErrors;
    };

    const cbackSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateCBACForm();

        if (Object.keys(validationErrors).length > 0) {

            setErrors(validationErrors);

            // Auto-open accordion and scroll to first error field
            scrollToErrorAndOpenAccordion(validationErrors);

            return;
        }

        setErrors({}); // Clear errors if valid

        try {
            setIsPageLoading(true);

            const payload = generatePayload();

            const response = await ncdAPI.submitCbac(payload, individualId);

            if (response?.success) {
                toast.success(`Cbac saved successfully!`);
                setIsPageLoading(false);
                onCBACSuccess?.(individualId);

                // Wait a moment before updating UI to let toast show
                setTimeout(() => {
                    hideSection("CBACKform");
                    setShowNcdScreening(true);
                    showSection("NCDScreening");
                    setTimeout(() => {
                        const section = document.querySelector(".accordian_section");
                        if (section) {
                            section.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                    }, 100);
                }, 1000); // 1 second delay
            } else {

                toast.error("Error in saving CBAC", { className: "custom-toast-error" });
                setIsPageLoading(false);
            }

        } catch (error) {

            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setIsPageLoading(false);
        }
    };

    const disabilityOptions = [
        { value: 'Visible defect', label: 'Visible defect' },
        { value: 'Known disability', label: 'Known disability' },
        { value: 'Bed ridden', label: 'Bed ridden' },
        { value: 'Require support for Activities of Daily Living', label: 'Require support for Activities of Daily Living' },
        { value: 'None', label: 'None Of the Above' }
    ];

    const fuelOptions = [
        { value: 'Firewood', label: 'Firewood' },
        { value: 'Crop residue', label: 'Crop residue' },
        { value: 'Cow dung cake', label: 'Cow dung cake' },
        { value: 'Coal', label: 'Coal' },
        { value: 'Kerosene', label: 'Kerosene' },
        { value: 'LPG', label: 'LPG' },
        { value: 'Other', label: 'Other' }
    ];

    const CheckboxOption = (props) => {
        return (
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                />
                {props.label}
            </components.Option>
        );
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////

    const getFinancialYearDetails = (cbacCurDate, isFYCbacComplete) => {
        if (!cbacCurDate) return { fyLabel: "", nextReviewDate: "" };

        // Convert DD-MM-YYYY → Date
        const [day, month, year] = cbacCurDate.split("-");
        const date = new Date(year, month - 1, day);

        const yr = date.getFullYear();
        const mn = date.getMonth() + 1;

        let startYear, endYear;

        if (mn >= 4) {
            startYear = yr;
            endYear = yr + 1;
        } else {
            startYear = yr - 1;
            endYear = yr;
        }

        let nextReviewDate = "";

        // ✅ Condition 1: FY Complete → Next review = 1 April
        if (isFYCbacComplete) {
            nextReviewDate = `1/4/${endYear}`;
        }
        // ✅ Condition 2: Not complete → Add 6 months
        else {
            const reviewDate = new Date(date);
            reviewDate.setMonth(reviewDate.getMonth() + 6);

            const d = reviewDate.getDate();
            const m = reviewDate.getMonth() + 1;
            const y = reviewDate.getFullYear();

            nextReviewDate = `${d}/${m}/${y}`;
        }

        return {
            fyLabel: `${startYear}-${endYear.toString().slice(-2)}`,
            nextReviewDate
        };
    };

    const { fyLabel, nextReviewDate } = getFinancialYearDetails(cbackdetail?.rskAssmntMdl?.cbacCurDate, cbackdetail?.isFYCbacComplete);

    ////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <section>

                {isPageLoading && <Loader />}

                <form onSubmit={cbackSubmit}>

                    <section className="first-section-ncdform clar-class">
                        <div className="accordianScreenHeader">

                            <h4 className="NCD-cbck-header for-mobile-header">
                                <span><img src={"/images/programs_logo/ncd.svg"} alt="" style={{ width: "43px", height: "43px", margin: "0 10px 0 0" }} /></span>
                                NCD CBAC
                            </h4>
                            <div className="closeDive" onClick={() => { hideSection('CBACKform'); setShowCBAC(false); }}><img src={"/images/closing.svg"} /></div>
                        </div>

                        <div className="row">

                            {cbackdetail?.isCbacComplete === true && (
                                <div className="alert alert-warning mt-2" style={{ padding: "8px 10px", margin: "20px 0" }}>
                                    CBAC already submitted for current FY {fyLabel}. It will be available for next review on {nextReviewDate}.
                                </div>
                            )}

                            <div className="col-md-6">
                                <div className="ng-star-inserted d-block">
                                    <span className="ng-star">* </span>All fields on this page are mandatory
                                    <input
                                        type="date"
                                        className="ng-date d-block mt-2"
                                        placeholder="choose date"
                                        name="cbacCurDate"
                                        value={cbacCurDate}
                                        onChange={handleDateChange}
                                        ref={cbacDateRef}
                                        min={formatToInputDate(famIndividualInfo?.additionalDetails?.enrollmentDate)}
                                        max={new Date().toISOString().split('T')[0]}
                                        disabled={cbackdetail?.isCbacComplete === true}
                                    />
                                    {errors.cbacCurDate && <span className="error-text">{errors.cbacCurDate}</span>}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-2 mt-2">
                                    <label className="form-label">Does this person have any of the following:</label>
                                </div>
                                <div className="mb-2" ref={disabilityTypeRef}>

                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        options={disabilityOptions}
                                        value={selectedOptions}
                                        onChange={handledisabilityType}
                                        components={{ Option: CheckboxOption }}
                                        classNamePrefix="react-select options-select"
                                        placeholder="Select"
                                        disabled={cbackdetail?.isCbacComplete === true}
                                        menuIsOpen={cbackdetail?.isCbacComplete ? false : undefined}
                                        onMenuOpen={() => {
                                            if (cbackdetail?.isCbacComplete) return;
                                        }}
                                    />
                                    {errors.disabilityType && <span className="error-text">{errors.disabilityType}</span>}
                                </div>
                                <div className="mb-2" ref={disabilityTypeDescRef}>
                                    <input
                                        type="text"
                                        name="disabilityTypeDesc"
                                        className="form-control border-0 border-bottom rounded-0 shadow-none"
                                        placeholder="If Yes specify"
                                        value={cbackdetail?.rskAssmntMdl?.disabilityTypeDesc}
                                        onChange={handleInputChange}
                                        disabled={disabilityType.includes("None")}
                                    />
                                    {errors.disabilityTypeDesc && <span className="error-text">{errors.disabilityTypeDesc}</span>}
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="accordion pad-20" id="cbacAccordion">

                        {/* Part A: Risk Assessment */}
                        <div className="accordion-item" ref={partARef}>
                            <h2 className="accordion-header" id="headingA">
                                <button
                                    className={`accordion-button fw-bold ${activeSection === "partA" ? '' : 'collapsed'}`}
                                    type="button"
                                    onClick={() => toggleSection("partA")}
                                >
                                    Part A: Risk Assessment
                                </button>
                            </h2>
                            <div
                                className={`accordion-collapse collapse ${activeSection === "partA" ? 'show' : ''}`}
                                aria-labelledby="headingA"
                            >
                                <div className="accordion-body">
                                    <div className="row mb-2">
                                        <div className="col-md-7 mb-1 d-flex align-items-center"><img src={"/images/age.png"} alt="Age-image" className="icon-age-image" /> What is your age? (in years)</div>
                                        <div className="col-md-5">
                                            <p>{age} Years <input type="hidden" name="age" value={age}></input> <span className="age_score">{ageScore}</span><Tooltip title="Risk Score for Age: 0-29 yrs = 0, 30-39 yrs = 1, 40-49 yrs = 2, 50-59 yrs = 3 and >=60 yrs = 4" arrow>
                                                <InfoOutlinedIcon
                                                    fontSize="small"
                                                    sx={{ color: 'black', ml: 1, cursor: 'pointer' }}
                                                />
                                            </Tooltip></p>
                                        </div>
                                        <div className="col-md-7 mb-1 d-flex align-items-center"><img src={"/images/smoking-gutka-khaini.png"} alt="" className="icon-age-image" /> Do you smoke or consume smokeless products such as gutka / khaini?</div>
                                        <div className="col-md-4">
                                            <select ref={smkngRef} className="form-select border-0 border-bottom rounded-0 shadow-none"
                                                onChange={handleSelectChange} name="smkng" value={smkng} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                <option value="Never">Never</option>
                                                <option value="Used to consume in the past/Sometimes now">Used to consume in the past/Sometimes now</option>
                                                <option value="Daily">Daily</option>
                                            </select>
                                            {errors.smkng && <span className="error-text">{errors.smkng}</span>}
                                        </div>
                                        <div className="col-md-1 mt-3">
                                            <span>{gutkaValue}</span>
                                        </div>
                                        <div className="col-md-7 mb-1 d-flex align-items-center"><img src={"/images/alcohol.png"} alt="" className="icon-age-image" /> Do you consume alcohol daily?</div>
                                        <div className="col-md-4">
                                            <select ref={drnkngRef}
                                                className="form-select border-0 border-bottom rounded-0 shadow-none"
                                                onChange={handleAlcoholChange} name="drnkng"
                                                value={drnkng} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                <option value="1">Yes</option>
                                                <option value="0">No</option>
                                            </select>
                                            {errors.drnkng && <span className="error-text">{errors.drnkng}</span>}
                                        </div>
                                        <div className="col-md-1 mt-3">
                                            <span>{drinkingscore}</span>
                                        </div>
                                        <div className="col-md-7 mb-1 d-flex align-items-center"><img src={"/images/measurement-of-waist.png"} alt="" className="icon-age-image" /> Measurement of waist (in cm)</div>
                                        <div className="col-md-4">
                                            <select ref={wstCirmfncRef} className="form-select border-0 border-bottom rounded-0 shadow-none"
                                                onChange={handleWaistChange} name="wstCirmfnc"
                                                value={wstCirmfnc} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                {isFemale ? (
                                                    <>
                                                        <option value="80 cm or less">
                                                            <span className="num-font">80</span> cm or less
                                                        </option>
                                                        <option value="81-90 cm">
                                                            <span className="num-font">81-90</span> cm
                                                        </option>
                                                        <option value="More than 90 cm">
                                                            More than <span className="num-font">90</span> cm
                                                        </option>
                                                    </>
                                                ) : (
                                                    <>
                                                        <option value="90 cm or less">
                                                            <span className="num-font">90</span> cm or less
                                                        </option>
                                                        <option value="91-100 cm">
                                                            <span className="num-font">91-100</span> cm
                                                        </option>
                                                        <option value="More than 100 cm">
                                                            More than <span className="num-font">100</span> cm
                                                        </option>
                                                    </>
                                                )}
                                            </select>
                                            {errors.wstCirmfnc && <span className="error-text">{errors.wstCirmfnc}</span>}
                                        </div>
                                        <div className="col-md-1 mt-3">
                                            <span>{waistconfirm}</span>
                                        </div>
                                        <div className="col-md-7 mb-1 d-flex align-items-center"><img src={"/images/physical-activity.png"} alt="" className="icon-age-image" /> Do you undertake and physical activities for minimum of 150 minutes in a week?</div>
                                        <div className="col-md-4">
                                            <select ref={minPhyscalActvtsRef} className="form-select border-0 border-bottom rounded-0 shadow-none"
                                                onChange={handlePhysicalActivitiesChange} name="minPhyscalActvts"
                                                value={minPhyscalActvts} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                <option value="0">At least <span className="num-font">150</span>mins</option>
                                                <option value="1">Less than <span className="num-font">150</span>mins</option>
                                            </select>
                                            {errors.minPhyscalActvts && <span className="error-text">{errors.minPhyscalActvts}</span>}
                                        </div>
                                        <div className="col-md-1 mt-3">
                                            <span>{minPhyscalActvtsScore}</span>
                                        </div>
                                        <div className="col-md-7 mb-1 d-flex align-items-center"><img src={"/images/anm.png"} alt="" className="icon-age-image" /> Do you have a family history (any one of your parents of siblings) of high blood pressure, diabetes or heart disease?</div>
                                        <div className="col-md-4">
                                            <select ref={fmlyHstryRef} className="form-select border-0 border-bottom rounded-0 shadow-none" onChange={handleFamilyHistoryChange} name="fmlyHstry"
                                                value={fmlyHstry} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                <option value="2">Yes</option>
                                                <option value="0">No</option>
                                            </select>
                                            {errors.fmlyHstry && <span className="error-text">{errors.fmlyHstry}</span>}
                                        </div>
                                        <div className="col-md-1 mt-3">
                                            <span>{familyscore}</span>
                                        </div>
                                    </div>
                                    <div className="Score_div">
                                        <h2>Risk Score - {riskAssessmentScore}</h2>
                                    </div>
                                    <p className="border-bottom-para">A score above 4 indicates that the person may be at risk for these NCDs and needs to be prioritized for attending the weekly NCD day.</p>
                                </div>
                            </div>
                        </div>

                        {/* Part B: Early Detection */}
                        <div className="accordion-item" ref={partBRef}>
                            <h2 className="accordion-header" id="headingB">
                                <button
                                    className={`accordion-button fw-bold ${activeSection === "partB" ? '' : 'collapsed'}`}
                                    type="button"
                                    onClick={() => toggleSection("partB")}
                                >
                                    Part B: Early Detection
                                </button>
                            </h2>
                            <div
                                className={`accordion-collapse collapse ${activeSection === "partB" ? 'show' : ''}`}
                                aria-labelledby="headingB"
                            >
                                <div className="accordion-body">
                                    <button type="button" className=" save-button mb-3 float-end" onClick={handleSelectAllNo} disabled={cbackdetail?.isCbacComplete === true}>None of the below</button>
                                    {earlyDetectionItems.map((item, index) => (
                                        <>
                                            {/* Add section header for B2 : Women Only */}
                                            {index === baseEarlyDetectionItems.length + (Number(age) >= 60 ? above60Items.length : 0) && isFemale && (
                                                <div className="early-detection-section-header" style={{ fontWeight: "bold", fontSize: "1.1rem", marginTop: "2rem" }}>
                                                    B2 : Women Only
                                                </div>
                                            )}

                                            {/* Add section header for 60 Years above */}
                                            {index === baseEarlyDetectionItems.length && Number(age) >= 60 && (
                                                <div className="early-detection-section-header" style={{ fontWeight: "bold", fontSize: "1.1rem", marginTop: "2rem" }}>
                                                    B3 : Elderly Specific (60 years and above)
                                                </div>
                                            )}

                                            <section className="margin-early-det">
                                                <div className="row" key={index}
                                                    style={{ marginTop: index === 0 ? "60px" : "0px" }}>
                                                    <div className="col-md-9 d-flex align-items-center">
                                                        <img src={earlyDetectionImages[index]} alt="" className="icon-age-image" />
                                                        {item.label}</div>
                                                    <div className="col-md-3">
                                                        <div className="radioFeildsValEarlyDetecion">
                                                            <div className="form-check form-check-inline mr-4">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={item.name}
                                                                    id={item.name + 'YES'}
                                                                    value="Yes"
                                                                    checked={earlyDetectionResponses[index] === "Yes"}
                                                                    onChange={() => handleRadioChange(index, "Yes")} disabled={cbackdetail?.isCbacComplete === true} />
                                                                <label className="form-check-label ms-2" htmlFor={item.name + 'YES'}>Yes</label>
                                                            </div>
                                                            <div className="form-check form-check-inline">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={item.name}
                                                                    id={item.name + 'NO'}
                                                                    value="No"
                                                                    checked={earlyDetectionResponses[index] === "No"}
                                                                    onChange={() => handleRadioChange(index, "No")} disabled={cbackdetail?.isCbacComplete === true} />
                                                                <label className="form-check-label ms-2" htmlFor={item.name + 'NO'}>No</label>
                                                            </div>

                                                            {errors.earlyDetection && errors.earlyDetection[index] && (
                                                                <div className="text-danger" style={{ fontSize: "0.9rem" }}>
                                                                    {errors.earlyDetection[index]}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                        </>
                                    ))}
                                    <p className="border-bottom-para">In case of individual answers YES to any of the above mentioned symptoms, refer the patient immediately to the nearest facility where a medical officer is available.</p>

                                </div>
                            </div>
                        </div>
                        {/* Part C: Which Type of fule */}
                        <div className="accordion-item" ref={partCRef}>
                            <h2 className="accordion-header" id="headingC">
                                <button
                                    className={`accordion-button fw-bold ${activeSection === "partC" ? '' : 'collapsed'}`}
                                    type="button"
                                    onClick={() => toggleSection("partC")}
                                >
                                    Part C: Occupational exposure
                                </button>
                            </h2>
                            <div
                                className={`accordion-collapse collapse ${activeSection === "partC" ? 'show' : ''}`}
                                aria-labelledby="headingC"
                            >
                                <div className="accordion-body">
                                    <div className="row mb-2">
                                        <div className="col-md-6 align-items-center">
                                            <span className="d-flex">  <img src={"/images/fuel.png"} alt="" className="icon-age-image" />
                                                <p className="mt-3">Type of Fuel Used for Cooking</p> </span>

                                            <Select
                                                isMulti
                                                closeMenuOnSelect={false}
                                                hideSelectedOptions={false}
                                                options={fuelOptions}
                                                value={fuelOptions.filter(option =>
                                                    fuel?.includes(option.value)
                                                )}
                                                onChange={handleFuel}
                                                components={{ Option: CheckboxOption }}
                                                classNamePrefix="react-select options-select"
                                                placeholder="Select"
                                                className="margin-input-cback"
                                                disabled={cbackdetail?.isCbacComplete === true}
                                                menuIsOpen={cbackdetail?.isCbacComplete ? false : undefined}
                                                onMenuOpen={() => {
                                                    if (cbackdetail?.isCbacComplete) return;
                                                }}
                                            />
                                            {errors.fuel && <span className="error-text">{errors.fuel}</span>}
                                            <div className="mt-3 margin-input-cback">
                                                <input
                                                    type="text"
                                                    className="form-control border-0 border-bottom rounded-0 shadow-none"
                                                    placeholder="If other, specify" name="fOther" onChange={handlefuelOther}
                                                    value={cbackdetail?.rskAssmntMdl?.fOther} readOnly={cbackdetail?.isCbacComplete === true}
                                                    disabled={!fuel?.includes('Other')}
                                                />

                                                {errors.fuelother && <span className="error-text">{errors.fuelother}</span>}
                                            </div>
                                        </div>

                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-4">
                                            <div className="occuptionExposureRadioBox">
                                                <img src={"/images/burning-of-garbage.png"} alt="" className="" />
                                                <div className="asideRadioOccptionBox">
                                                    <label className="form-label"> Crop Residue Burning</label>
                                                    <div className="d-flex gap-4">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="cropResidue"
                                                                id="cropBurningYes"
                                                                value="yes"
                                                                onChange={handleRadioenexpChange}
                                                                checked={cropResidue === true}
                                                                disabled={cbackdetail?.isCbacComplete === true}
                                                            />
                                                            <label className="form-check-label" htmlFor="cropBurningYes">
                                                                Yes
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="cropResidue"
                                                                id="cropBurningNo"
                                                                value="no"
                                                                onChange={handleRadioenexpChange}
                                                                checked={cropResidue === false}
                                                                disabled={cbackdetail?.isCbacComplete === true}
                                                            />
                                                            <label className="form-check-label" htmlFor="cropBurningNo">
                                                                No
                                                            </label>
                                                        </div>
                                                        {errors.cropResidue && <span className="error-text">{errors.cropResidue}</span>}
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        <div className="col-md-4">
                                            <div className="occuptionExposureRadioBox">
                                                <img src={"/images/burning-of-garbage.png"} alt="" className="" />
                                                <div className="asideRadioOccptionBox">
                                                    <label className="form-label">Burning garbage leaves</label>
                                                    <div className="d-flex gap-4 ">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="brngGarbg"
                                                                id="garbageBurningYes"
                                                                value="yes"
                                                                onChange={handleRadioenexpChange}
                                                                checked={brngGarbg === true}
                                                                disabled={cbackdetail?.isCbacComplete === true}
                                                            />
                                                            <label className="form-check-label" htmlFor="garbageBurningYes">
                                                                Yes
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="brngGarbg"
                                                                id="garbageBurningNo"
                                                                value="no"
                                                                onChange={handleRadioenexpChange}
                                                                checked={brngGarbg === false}
                                                                disabled={cbackdetail?.isCbacComplete === true}
                                                            />
                                                            <label className="form-check-label" htmlFor="garbageBurningNo">
                                                                No
                                                            </label>
                                                        </div>
                                                        {errors.brngGarbg && <span className="error-text">{errors.brngGarbg}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="occuptionExposureRadioBox">
                                                <img src={"/images/working-in-industry.png"} alt="" className="" />
                                                <div className="asideRadioOccptionBox">
                                                    <label className="form-label ">Working in industries with smoke gas</label>
                                                    <div className="d-flex gap-4 ">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="smokeExpsure"
                                                                id="smokegasYes"
                                                                value="yes"
                                                                onChange={handleRadioenexpChange}
                                                                checked={smokeExpsure === true}
                                                                disabled={cbackdetail?.isCbacComplete === true}
                                                            />
                                                            <label className="form-check-label" htmlFor="smokegasYes">
                                                                Yes
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="smokeExpsure"
                                                                id="smokegasNo"
                                                                value="no"
                                                                onChange={handleRadioenexpChange}
                                                                checked={smokeExpsure === false}
                                                                disabled={cbackdetail?.isCbacComplete === true}
                                                            />
                                                            <label className="form-check-label" htmlFor="smokegasNo">
                                                                No
                                                            </label>
                                                        </div>

                                                        {errors.smokeExpsure && <span className="error-text">{errors.smokeExpsure}</span>}
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Part D: PHQ2 */}
                        <div className="accordion-item" ref={partDRef}>
                            <h2 className="accordion-header" id="headingD">
                                <button
                                    className={`accordion-button fw-bold ${activeSection === "partD" ? '' : 'collapsed'}`}
                                    type="button"
                                    onClick={() => toggleSection("partD")}
                                >
                                    Part D: PHQ2
                                </button>
                            </h2>
                            <div
                                className={`accordion-collapse collapse ${activeSection === "partD" ? 'show' : ''}`}
                                aria-labelledby="headingD"
                            >

                                <div className="accordion-body">
                                    <h6>Over the last 2 weeks how often have you been bothered with following problems:</h6>

                                    <div className="row mb-2">
                                        <div className="col-md-7 mt-4">
                                            <img src={"/images/little_interest_or_pleasure_doing_things.png"} alt="" className="icon-age-image" />
                                            Little interest or pleasure in doing things
                                        </div>
                                        <div className="col-md-4 mt-5">
                                            <select ref={mentalHealthRef} className="form-select border-0 border-bottom rounded-0 shadow-none" name="littlePleasure" value={littlePleasure} onChange={handleSelectionChange} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                <option value="Not at all">Not at all</option>
                                                <option value="Several days">Several days</option>
                                                <option value="More than half the days">More than half the days</option>
                                                <option value="Nearly everyday">Nearly everyday</option>
                                            </select>

                                        </div>
                                        <div className="col-md-1 mt-5">
                                            <span>{littleScore}</span>
                                        </div>
                                    </div>

                                    <div className="row mb-2">
                                        <div className="col-md-7 mt-4">
                                            <img src={"/images/little_interest_or_pleasure_doing_things.png"} alt="" className="icon-age-image" />
                                            Feeling down, depressed, or hopeless
                                        </div>
                                        <div className="col-md-4 mt-5">
                                            <select className="form-select border-0 border-bottom rounded-0 shadow-none" name="feelDepressed" value={feelDepressed} onChange={handleSelectionChange} disabled={cbackdetail?.isCbacComplete === true}>
                                                <option value="">Select</option>
                                                <option value="Not at all">Not at all</option>
                                                <option value="Several days">Several days</option>
                                                <option value="More than half the days">More than half the days</option>
                                                <option value="Nearly everyday">Nearly everyday</option>
                                            </select>

                                        </div>
                                        <div className="col-md-1 mt-5">
                                            <span>{feelDepressedScore}</span>
                                        </div>
                                    </div>

                                    {errors.mentalHealth && <span className="error-text">{errors.mentalHealth}</span>}

                                    <div className="Score_div">
                                        <h2>Risk Score - {dangerScore}</h2>
                                    </div>
                                    <p className="border-bottom-para">Anyone with total score greater than 3 should be referred to CHO/ MO (PHC/UPHC)</p>
                                    {/* Conditional Message if Danger Score is 4 or greater */}
                                    {dangerScore >= 4 && (
                                        <p className="text-danger fw-bold">
                                            The patient is referred for symptoms related to Mental Health
                                        </p>
                                    )}
                                </div>

                            </div>

                        </div>

                        {cbackdetail && cbackdetail.isCbacComplete === false && (
                            <div className="cback_save_button"><button type="submit">SAVE &amp; CONTINUE</button> </div>
                        )}

                    </div>

                </form>
            </section >
        </>
    );
}
